import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { SearchField, EmptyState, Skeleton, InfiniteSentinel, InlineAlert, AlertToast, SegmentedControl, Spinner, Button } from '../../ui';
import { GuestSongRow } from './GuestSongRow';
import { GuestCodePad } from './GuestCodePad';
import { guestResultsAnnouncement } from './guestResultsAnnouncement.ts';
import { shouldFocusGuestSearch } from './guestSearchHotkey.ts';
import { resolveGuestResultsNav, applyGuestNavIntent } from './guestResultsNav.ts';
import { isTypingTarget } from '../../lib/isTypingTarget.ts';
import { useT } from '../../i18n/i18n.tsx';
import type { MessageKey } from '../../i18n/messages.en.ts';
import type { Song } from '../../api/types';

/** Which door the guest is using. `'code'` is the default (see `GuestFinderProps.mode`). */
export type GuestFindMode = 'code' | 'search';

export interface GuestSearchProps {
  /**
   * MVP-274 R1. Before this task the page was one search field over a result list, and that was the
   * ONLY way in — no browse, no code entry, nothing. For the customer this product actually has
   * (older, on a phone, in a dark room, holding the venue's printed song book) free-text search is
   * the hardest input the product could ask for, and every song they are looking at already has its
   * number printed next to it. `'code'` leads because that number is the shortest path from the book
   * in their hand to the song on the screen; `'search'` is still here, it just stopped being the
   * only door.
   */
  mode: GuestFindMode;
  onModeChange: (mode: GuestFindMode) => void;
  /** Search mode's free text. */
  query: string;
  onQueryChange: (value: string) => void;
  /** Code mode's digits. Kept separate from `query` so switching doors does not destroy what was
   * typed behind the other one — a customer who tries a code, gives up and searches by title should
   * find their half-typed code still there if they switch back. */
  code: string;
  onCodeChange: (value: string) => void;
  songs: Song[];
  loading: boolean;
  errorMessage: string | null;
  /** MVP-11 #13: the guest page's one error slot is dismissable like every other transient
   * failure — optional so tests/callers that never pass an error need not pass this either. */
  onDismissError?: () => void;
  onPick: (song: Song) => void;
  /** True when the list below is the whole catalogue in code order rather than a result set — the
   * browse fallback for a customer with no book and no title in mind (MVP-274 R1). */
  browsing?: boolean;
  /** Browse only: another page of the catalogue exists. Search results never paginate. */
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  /** Browse only: why the last automatic page failed. Its presence is what stops the sentinel
   * asking again — see the render below. */
  moreError?: string | null;
}

/**
 * DESIGN.md §9.3 [Requests] — the guest's way into the catalogue, and the results it produces
 * (DESIGN.md §10's "Guest (360–768 px)" layout). Purely presentational: `RequestPage` owns the
 * debounce, the AbortController and the actual `guestSearch()` call, exactly like library-ui's
 * `LibraryScreen` owns its own search effect around a dumb results view.
 *
 * Both doors write into ONE result list, so there is one search effect, one empty state and one row
 * component no matter how the customer got here.
 */
export function GuestSearch({
  mode,
  onModeChange,
  query,
  onQueryChange,
  code,
  onCodeChange,
  songs,
  loading,
  errorMessage,
  onDismissError,
  onPick,
  browsing = false,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  moreError = null,
}: GuestSearchProps) {
  const t = useT();
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // AC-REQ-2's "QR scan -> search box focused and interactive" — still true, but only when the
  // search field is the door on screen. In code mode there is no field to focus and summoning the
  // OS keyboard over the pad would defeat the point of having one (see `GuestCodePad`).
  useEffect(() => {
    if (mode === 'search') inputRef.current?.focus();
  }, [mode]);

  // MVP-490. `/` takes the guest to the search field from wherever they are — the room-based venue
  // has a PC with a keyboard in every room, and hunting for the box with a mouse is the cost this
  // removes. See `guestSearchHotkey.ts` for why the match is on `key` and ignores Shift.
  //
  // A second document listener rather than a branch inside `GuestCodePad`'s: the pad UNMOUNTS in
  // search mode, so folding `/` into its handler would make the hotkey vanish exactly when the
  // guest is searching. The two listeners cannot collide — `keyFromKeyboardEvent` returns null for
  // '/', and neither handler calls `stopPropagation`.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      if (
        !shouldFocusGuestSearch(e, {
          isTypingTarget: isTypingTarget(target),
          isDialogOpen: document.querySelector('[role="dialog"]') !== null,
        })
      )
        return;
      e.preventDefault(); // Firefox opens its quick-find bar on '/', on top of ours.
      // Both lines run unconditionally, no branch. The focus call only LOOKS redundant next to the
      // `[mode]` effect above: that effect fires when the mode FLIPS, which covers `/` pressed on
      // the pad; this line covers the case it cannot — already in search mode with focus elsewhere,
      // e.g. the guest tapped a song row, dismissed the sheet and came back. Delete either and one
      // of the two cases stops working.
      onModeChange('search');
      inputRef.current?.focus();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mode, onModeChange]);

  // MVP-491. Up/Down (and Home/End) walk the result list, which is what anyone expects of a list of
  // results and what the console already gives the operator. Bound to this subtree's own React
  // handler rather than to `document` like the `/` hotkey above: arrows are how a keyboard-only
  // guest scrolls the page, so a document listener would have to guess when they were meant for the
  // list. Bubbling from the focused row or field answers that by itself.
  //
  // The rows are read out of the DOM at keydown time instead of being held in refs. They are
  // `GuestSongRow` buttons rendered from `songs.map`, so a ref array would have to be kept in step
  // with a list that replaces itself on every keystroke and grows on every infinite-scroll page;
  // the DOM already knows the answer, and it is the same order.
  function onNavKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    const root = rootRef.current;
    if (!root) return;
    const rows = Array.from(root.querySelectorAll<HTMLElement>('.guest-song-row'));
    const active = document.activeElement as HTMLElement | null;
    const intent = resolveGuestResultsNav(e, {
      count: rows.length,
      activeIndex: active ? rows.indexOf(active) : -1,
      isSearchFieldFocused: active !== null && active === inputRef.current,
      // MVP-496: the shared predicate, the same one the operator hotkeys use. It counts a focused
      // `<select>` as typing, which this handler needs more than any other caller does — a select
      // owns Up/Down, and Up/Down here walk the result list.
      isTypingTarget: isTypingTarget(active),
      isDialogOpen: document.querySelector('[role="dialog"]') !== null,
      hasSearchField: inputRef.current !== null,
    });
    // `preventDefault` only once something was actually focused: an intent naming a row that has
    // since gone must leave the key to the browser rather than swallow it into nothing.
    if (applyGuestNavIntent(intent, { rows, searchField: inputRef.current, queueCard: null })) e.preventDefault();
  }

  const trimmedQuery = query.trim();
  const emptyStateCopy = describeEmptyState(mode, trimmedQuery, code);
  // MVP-335: the results list replaces itself on every keystroke, code digit and "Show more" tap,
  // and none of that was ever announced — this is the one place that decides what to say (see
  // `guestResultsAnnouncement`'s own doc comment for why it returns `null` for the error/skeleton
  // states below).
  const resultsAnnouncement = guestResultsAnnouncement(songs.length, { loading, hasError: Boolean(errorMessage) });

  return (
    <div className="guest-search" ref={rootRef} onKeyDown={onNavKeyDown}>
      <div className="guest-search__bar">
        <SegmentedControl<GuestFindMode>
          value={mode}
          onChange={onModeChange}
          label={t('guest.find.modeLabel')}
          options={[
            { value: 'code', label: t('guest.find.modeCode') },
            { value: 'search', label: t('guest.find.modeSearch') },
          ]}
        />
        {mode === 'code' ? (
          <GuestCodePad code={code} onCodeChange={onCodeChange} />
        ) : (
          /* `shortcutHint="/"` is the only thing that makes the hotkey above discoverable.
             Untranslated by the standing keycap rule (`features/ops/hotkeys.ts`'s
             `formatBindingLabel`) — it names a key the customer is looking at. It renders as `Kbd`,
             which is `text-meta` (14px, so the type floor holds) and not interactive, so the 44px
             guest floor does not apply. Accepted: a phone guest sees a small `/` glyph that means
             nothing to them. */
          <SearchField
            ref={inputRef}
            value={query}
            onChange={onQueryChange}
            label={t('guest.search.label')}
            placeholder={t('guest.search.placeholder')}
            clearLabel={t('guest.search.clear')}
            shortcutHint="/"
            autoComplete="off"
          />
        )}
      </div>

      {/* MVP-335: visually hidden and separate from the visible list below it, so the announcement
          text can change on every keystroke without a sighted user seeing a flicker of status text
          they never asked for — `aria-live="polite"` waits for the guest to pause typing rather
          than interrupting mid-word. */}
      <p className="visually-hidden" role="status" aria-live="polite">
        {resultsAnnouncement ? t(resultsAnnouncement.key, resultsAnnouncement.params) : ''}
      </p>

      <div className="guest-search__results">
        {/* The browse list needs saying out loud: without it, a customer who has typed nothing sees
            2,209 songs appear and has no way to know what order they are in or why these ones. */}
        {browsing && songs.length > 0 && <p className="guest-search__browse-note text-meta">{t('guest.find.browseNote')}</p>}

        {errorMessage ? (
          <AlertToast variant="danger" onDismiss={onDismissError} dismissLabel={t('common.dismiss')}>
            {errorMessage}
          </AlertToast>
        ) : loading && songs.length === 0 ? (
          <div className="guest-search__skeletons" aria-hidden="true">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="row" style={{ height: 'var(--control-h-lg)' }} />
            ))}
          </div>
        ) : songs.length === 0 ? (
          <EmptyState
            icon={mode === 'code' ? 'keyboard' : 'search'}
            title={t(emptyStateCopy.titleKey, emptyStateCopy.params)}
            description={t(emptyStateCopy.detailKey)}
          />
        ) : (
          <>
            <ul className="guest-song-list">
              {songs.map((song) => (
                <li key={song.documentId}>
                  {/* Highlighting the code the customer just typed would double up with the `#NNNN`
                      the row now prints, so only free text ever highlights. */}
                  <GuestSongRow song={song} query={mode === 'search' ? query : ''} onPick={onPick} />
                </li>
              ))}
            </ul>
            {/* MVP-451. The KJ's catalogue has loaded on scroll since MVP-230; this list asked for a
                button press per 40 songs. The customer is one-thumbed on a phone in a dark room —
                scrolling is the cheapest gesture they have, and stopping to aim at a button is an
                interruption at exactly the rhythm of browsing.

                MVP-230's other half still applies and is the reason `moreError` disables the
                sentinel: nobody DECIDES to try again in an infinite scroll, so a backend that is
                down would turn the end of the list into a request loop that re-fires on every
                scroll nudge. A failed page stops automatic loading and waits to be asked. */}
            {hasMore && onLoadMore && (
              <div className="guest-search__more">
                <InfiniteSentinel onReached={onLoadMore} disabled={loadingMore || moreError !== null} />
                {moreError ? (
                  <>
                    <InlineAlert variant="danger">{moreError}</InlineAlert>
                    <Button variant="secondary" fullWidth loading={loadingMore} onClick={onLoadMore}>
                      {t('common.retry')}
                    </Button>
                  </>
                ) : (
                  // `role="status"`: the list grew under a reader who cannot see it grow. Always
                  // rendered while there is more, not only while a request is in flight — a row that
                  // appears and disappears at the end of a list is a row that moves the list.
                  <div className="guest-search__more-pending" role="status">
                    <Spinner label={t('common.loading')} />
                    <span className="text-meta">{t('guest.find.loadingMore')}</span>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** Which "nothing to show" sentence belongs to this door and this input. Four genuinely different
 * situations, and telling a customer who mistyped a code to "try a different spelling" would send
 * them looking for a problem they do not have. */
function describeEmptyState(
  mode: GuestFindMode,
  trimmedQuery: string,
  code: string
): { titleKey: MessageKey; detailKey: MessageKey; params?: Record<string, string | number> } {
  if (mode === 'code') {
    return code
      ? { titleKey: 'guest.code.noMatchTitle', detailKey: 'guest.code.noMatchHint', params: { code } }
      : { titleKey: 'guest.code.emptyTitle', detailKey: 'guest.code.emptyHint' };
  }
  return trimmedQuery
    ? { titleKey: 'guest.search.noMatchesTitle', detailKey: 'guest.search.noMatchesHint', params: { query: trimmedQuery } }
    : { titleKey: 'guest.search.emptyTitle', detailKey: 'guest.search.emptyHint' };
}
